import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import DatabaseService from "../../DatabaseService";
import { apiResponse } from "../../APIResponse";

/**
 * Fetch all databases
 */
export const DatabaseGetList = createAsyncThunk(
  "DatabaseGetList",
  async (_, { dispatch }) => {
    try {
      dispatch(setDatabaseLoader(true));
      const resData = await DatabaseService.getAllDatabases();
      const response = apiResponse(false, resData);
      dispatch(setDatabaseLoader(false));
      return response;
    } catch (error) {
      dispatch(setDatabaseLoader(false));
      return { isValidate: false, message: error?.message || "Failed to fetch databases" };
    }
  }
);

/**
 * Fetch database by ID
 */
export const DatabaseGetById = createAsyncThunk(
  "DatabaseGetById",
  async (id, { dispatch }) => {
    try {
      dispatch(setDatabaseLoader(true));
      const resData = await DatabaseService.getDatabaseById(id);
      const response = apiResponse(false, resData);
      dispatch(setDatabaseLoader(false));
      return response;
    } catch (error) {
      dispatch(setDatabaseLoader(false));
      return { isValidate: false, message: error?.message || "Failed to fetch database" };
    }
  }
);

/**
 * Fetch database status
 */
export const DatabaseGetStatus = createAsyncThunk(
  "DatabaseGetStatus",
  async (id, { dispatch }) => {
    try {
      const resData = await DatabaseService.getDatabaseStatus(id);
      const response = apiResponse(false, resData);
      return { id, ...response };
    } catch (error) {
      return { id, isValidate: false, message: error?.message || "Failed to fetch status" };
    }
  }
);

/**
 * Fetch all monitoring data in one call
 */
export const DatabaseGetMonitoringAll = createAsyncThunk(
  "DatabaseGetMonitoringAll",
  async (id, { dispatch }) => {
    try {
      dispatch(setDatabaseLoader(true));
      const resData = await DatabaseService.getMonitoringAll(id);
      const response = apiResponse(false, resData);
      dispatch(setDatabaseLoader(false));
      return { id, ...response };
    } catch (error) {
      dispatch(setDatabaseLoader(false));
      return { id, isValidate: false, message: error?.message || "Failed to fetch monitoring data" };
    }
  }
);

/**
 * Create new database
 */
export const DatabaseCreate = createAsyncThunk(
  "DatabaseCreate",
  async (data, { dispatch }) => {
    try {
      dispatch(setDatabaseActionLoader(true));

      // Transform frontend format to API format
      const apiData = {
        name: data.name,
        type: data.type || "postgresql",
        version: data.version || "16",
        tier: data.tier || "small",
        database: data.database || "appdb",
        username: data.username,
        password: data.password,
        port: data.port || null,
        storage: data.storage || "20Gi",
        environment: data.environment || "development",
        tags: data.tags || {},
        resources: data.resources || null,
        // Pass input_data for machine configuration
        input_data: data.input_data || null,
      };

      const resData = await DatabaseService.createDatabase(apiData);
      const response = apiResponse(true, resData);
      dispatch(setDatabaseActionLoader(false));
      return response;
    } catch (error) {
      dispatch(setDatabaseActionLoader(false));
      return { isValidate: false, message: error?.message || "Failed to create database" };
    }
  }
);

/**
 * Delete database
 */
export const DatabaseDelete = createAsyncThunk(
  "DatabaseDelete",
  async ({ id, password }, { dispatch }) => {
    try {
      dispatch(setDatabaseActionLoader(true));
      const resData = await DatabaseService.deleteDatabase(id, password);
      const response = apiResponse(true, resData);
      dispatch(setDatabaseActionLoader(false));
      return { id, ...response };
    } catch (error) {
      dispatch(setDatabaseActionLoader(false));
      return { id, isValidate: false, message: error?.message || "Failed to delete database" };
    }
  }
);

/**
 * Start database
 */
export const DatabaseStart = createAsyncThunk(
  "DatabaseStart",
  async (id, { dispatch }) => {
    try {
      dispatch(setDatabaseActionLoader(true));
      dispatch(setActionDbId(id));
      const resData = await DatabaseService.startDatabase(id);
      const response = apiResponse(true, resData);
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, ...response };
    } catch (error) {
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, isValidate: false, message: error?.message || "Failed to start database" };
    }
  }
);

/**
 * Stop database
 */
export const DatabaseStop = createAsyncThunk(
  "DatabaseStop",
  async (id, { dispatch }) => {
    try {
      dispatch(setDatabaseActionLoader(true));
      dispatch(setActionDbId(id));
      const resData = await DatabaseService.stopDatabase(id);
      const response = apiResponse(true, resData);
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, ...response };
    } catch (error) {
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, isValidate: false, message: error?.message || "Failed to stop database" };
    }
  }
);

/**
 * Restart database
 */
export const DatabaseRestart = createAsyncThunk(
  "DatabaseRestart",
  async (id, { dispatch }) => {
    try {
      dispatch(setDatabaseActionLoader(true));
      dispatch(setActionDbId(id));
      const resData = await DatabaseService.restartDatabase(id);
      const response = apiResponse(true, resData);
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, ...response };
    } catch (error) {
      dispatch(setDatabaseActionLoader(false));
      dispatch(setActionDbId(null));
      return { id, isValidate: false, message: error?.message || "Failed to restart database" };
    }
  }
);

/**
 * Get database types
 */
export const DatabaseGetTypes = createAsyncThunk(
  "DatabaseGetTypes",
  async (_, { dispatch }) => {
    try {
      const resData = await DatabaseService.getDatabaseTypes();
      const response = apiResponse(false, resData);
      return response;
    } catch (error) {
      return { isValidate: false, message: error?.message || "Failed to fetch database types" };
    }
  }
);

/**
 * Get database versions by type
 */
export const DatabaseGetVersions = createAsyncThunk(
  "DatabaseGetVersions",
  async (type, { dispatch }) => {
    try {
      const resData = await DatabaseService.getDatabaseVersions(type);
      const response = apiResponse(false, resData);
      return { type, ...response };
    } catch (error) {
      return { type, isValidate: false, message: error?.message || "Failed to fetch versions" };
    }
  }
);

/**
 * Get database tiers by type
 */
export const DatabaseGetTiers = createAsyncThunk(
  "DatabaseGetTiers",
  async (type, { dispatch }) => {
    try {
      const resData = await DatabaseService.getDatabaseTiers(type);
      const response = apiResponse(false, resData);
      return { type, ...response };
    } catch (error) {
      return { type, isValidate: false, message: error?.message || "Failed to fetch tiers" };
    }
  }
);

// Initial state
const initialState = {
  // Data
  databases: [],
  currentDatabase: null,
  currentStatus: null,
  currentMetrics: null,
  currentConnections: null,
  databaseTypes: [],
  databaseVersions: {},
  databaseTiers: {},

  // Loading states
  isLoading: false,
  isActionLoading: false,
  actionDbId: null,

  // Response data for components to read
  DatabaseGetListData: null,
  DatabaseGetByIdData: null,
  DatabaseCreateData: null,
  DatabaseDeleteData: null,

  // Error
  error: null,
};

// Create slice
const DatabaseSlice = createSlice({
  name: "databaseredux",
  initialState,
  reducers: {
    setDatabaseLoader: (state, action) => {
      state.isLoading = action.payload;
    },
    setDatabaseActionLoader: (state, action) => {
      state.isActionLoading = action.payload;
    },
    setActionDbId: (state, action) => {
      state.actionDbId = action.payload;
    },
    clearCurrentDatabase: (state) => {
      state.currentDatabase = null;
      state.currentStatus = null;
      state.currentMetrics = null;
      state.currentConnections = null;
      state.DatabaseGetByIdData = null;
    },
    clearDatabaseErrors: (state) => {
      state.error = null;
    },
    setDatabaseGetListData: (state, action) => {
      state.DatabaseGetListData = action.payload || null;
    },
    updateDatabaseStatus: (state, action) => {
      const { id, status } = action.payload;
      const database = state.databases.find((db) => db.id === id);
      if (database) {
        database.status = status;
      }
      if (state.currentDatabase?.id === id) {
        state.currentDatabase.status = status;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // DatabaseGetList
      .addCase(DatabaseGetList.fulfilled, (state, action) => {
        state.DatabaseGetListData = action.payload;
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.databases = action.payload.data.data;
        }
      })

      // DatabaseGetById
      .addCase(DatabaseGetById.fulfilled, (state, action) => {
        state.DatabaseGetByIdData = action.payload;
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.currentDatabase = action.payload.data.data;
        }
      })

      // DatabaseGetStatus
      .addCase(DatabaseGetStatus.fulfilled, (state, action) => {
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.currentStatus = action.payload.data.data;
        }
      })

      // DatabaseGetMonitoringAll
      .addCase(DatabaseGetMonitoringAll.fulfilled, (state, action) => {
        if (action.payload?.isValidate && action.payload?.data) {
          const monitoringData = action.payload.data;
          if (monitoringData.database) {
            state.currentDatabase = monitoringData.database;
          }
          if (monitoringData.status) {
            state.currentStatus = monitoringData.status;
          }
          if (monitoringData.metrics) {
            state.currentMetrics = monitoringData.metrics;
          }
          if (monitoringData.connections) {
            state.currentConnections = monitoringData.connections;
          }
        }
      })

      // DatabaseCreate
      .addCase(DatabaseCreate.fulfilled, (state, action) => {
        state.DatabaseCreateData = action.payload;
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.databases.unshift(action.payload.data.data);
        }
      })

      // DatabaseDelete
      .addCase(DatabaseDelete.fulfilled, (state, action) => {
        state.DatabaseDeleteData = action.payload;
        if (action.payload?.isValidate) {
          state.databases = state.databases.filter((db) => db.id !== action.payload.id);
        }
      })

      // DatabaseStart
      .addCase(DatabaseStart.fulfilled, (state, action) => {
        if (action.payload?.isValidate) {
          const database = state.databases.find((db) => db.id === action.payload.id);
          if (database) {
            database.status = "starting";
          }
          if (state.currentDatabase?.id === action.payload.id) {
            state.currentDatabase.status = "starting";
          }
        }
      })

      // DatabaseStop
      .addCase(DatabaseStop.fulfilled, (state, action) => {
        if (action.payload?.isValidate) {
          const database = state.databases.find((db) => db.id === action.payload.id);
          if (database) {
            database.status = "stopping";
          }
          if (state.currentDatabase?.id === action.payload.id) {
            state.currentDatabase.status = "stopping";
          }
        }
      })

      // DatabaseGetTypes
      .addCase(DatabaseGetTypes.fulfilled, (state, action) => {
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.databaseTypes = action.payload.data.data;
        }
      })

      // DatabaseGetVersions
      .addCase(DatabaseGetVersions.fulfilled, (state, action) => {
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.databaseVersions[action.payload.type] = action.payload.data.data;
        }
      })

      // DatabaseGetTiers
      .addCase(DatabaseGetTiers.fulfilled, (state, action) => {
        if (action.payload?.isValidate && action.payload?.data?.data) {
          state.databaseTiers[action.payload.type] = action.payload.data.data;
        }
      });
  },
});

export const {
  setDatabaseLoader,
  setDatabaseActionLoader,
  setActionDbId,
  clearCurrentDatabase,
  clearDatabaseErrors,
  setDatabaseGetListData,
  updateDatabaseStatus,
} = DatabaseSlice.actions;

export default DatabaseSlice.reducer;
